/**
 * "Desteklenen biçimler" - the real answer, from the server.
 *
 * Opened from the import area so nobody has to leave the screen they are
 * uploading on to find out whether their camera's files will work.
 *
 * Two things it says that a marketing list would not:
 *
 * **Which RAW families.** "RAW" is not a format, it is a dozen incompatible
 * camera formats, and a product that prints the word alone is promising all of
 * them. This prints the extensions actually accepted.
 *
 * **That SVG stops short of reconstruction.** A rasterised vector drawing can
 * be imported, previewed and analysed, and has no photogrammetric meaning at
 * all - so it is listed with what it can do rather than in the same
 * undifferentiated bucket as a photograph. Collapsing every accepted format
 * into one word is how "supported" stops meaning anything.
 */

import { useRef } from "react";
import type { FormatCapability } from "@/api/types";
import { Loading } from "@/components/ui";
import { useMediaCapabilities } from "@/lib/useMediaCapabilities";
import "./sessions.css";
import { useDialogFocus } from "@/lib/useDialogFocus";

/** Only levels below the full pipeline are worth calling out. */
const SUPPORT_NOTE: Partial<Record<FormatCapability["support"], string>> = {
  import: "Yalnızca içe aktarılır",
  preview: "İçe aktarılır ve önizlenir",
  analyze: "Analiz edilir, rekonstrüksiyonda kullanılmaz",
};

function FormatList({ title, formats }: { title: string; formats: FormatCapability[] }) {
  if (formats.length === 0) return null;
  return (
    <div className="formats__group">
      <h3 className="formats__group-title">{title}</h3>
      <ul className="formats__list">
        {formats.map((format) => (
          <li key={format.label} className="formats__row">
            <span className="formats__label">{format.label}</span>
            <span className="formats__ext">
              {format.extensions.map((extension) => `.${extension}`).join(" ")}
            </span>
            {(SUPPORT_NOTE[format.support] || format.note) && (
              <span className="formats__note">
                {[SUPPORT_NOTE[format.support], format.note]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function SupportedFormats({ onClose }: { onClose: () => void }) {
  const capabilities = useMediaCapabilities();
  const dialog = useRef<HTMLDivElement>(null);
  const closer = useRef<HTMLButtonElement>(null);

  // The close button, not the panel: this one is a reference list, and the
  // useful first stop is the way out of it.
  useDialogFocus(closer, { onEscape: onClose });

  return (
    <div
      className="formats__backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="formats"
        role="dialog"
        aria-modal="true"
        aria-labelledby="formats-title"
        ref={dialog}
      >
        <header className="formats__head">
          <h2 id="formats-title">Desteklenen biçimler</h2>
          <button
            type="button"
            className="formats__close"
            onClick={onClose}
            ref={closer}
            aria-label="Kapat"
          >
            ×
          </button>
        </header>

        {!capabilities.loaded ? (
          <Loading label="Biçimler okunuyor" />
        ) : (
          <>
            <FormatList title="Fotoğraf" formats={capabilities.photos} />
            <FormatList title="Video" formats={capabilities.videos} />
            <p className="muted small">
              Bu liste sunucunun gerçekten kabul ettiği biçimlerden üretilir.
              Dosyalar yalnızca uzantısına göre değil, içeriği çözümlenerek
              kabul edilir.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
